import React, { useEffect, useMemo, useState } from "react";
import { resolveImage } from "../Utils/imageUtils";
import { crudApi } from "../services/api";
import { REF_CASCADE } from "./adminFieldSchemas";
import AdminImageUpload from "./AdminImageUpload";

const isFullWidthField = (field) =>
  field.type === "textarea" ||
  field.type === "lines" ||
  field.type === "kvLines" ||
  field.type === "json" ||
  field.type === "refMulti" ||
  field.name === "status" ||
  field.name === "images";

const groupFieldsForRender = (fields) => {
  const groups = [];
  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    if (field.type === "section") {
      if (field.categorySection && fields[i + 1]?.categoryField) {
        const catFields = [];
        i += 1;
        while (i < fields.length && fields[i].categoryField) {
          catFields.push(fields[i]);
          i += 1;
        }
        groups.push({ type: "categoryBlock", section: field, fields: catFields });
        continue;
      }
      groups.push({ type: "section", field });
      i += 1;
      continue;
    }
    if (field.type === "boolean") {
      const toggles = [];
      while (i < fields.length && fields[i].type === "boolean") {
        toggles.push(fields[i]);
        i += 1;
      }
      groups.push({ type: "toggleGroup", fields: toggles });
      continue;
    }
    groups.push({ type: "field", field });
    i += 1;
  }
  return groups;
};

const getRefLabel = (item, resource) => {
  if (resource === "cars") {
    const brand = item.brand?.name || "";
    const loc = item.location || "";
    const price = item.price != null ? `₹${item.price}` : "";
    return [loc, brand, price].filter(Boolean).join(" · ") || item._id;
  }
  if (resource === "users") return item.email || item.name || item._id;
  if (resource === "cities") {
    const stateName = item.state?.name;
    return stateName ? `${item.name} (${stateName})` : item.name || item._id;
  }
  return item.name || item.email || item.title || item.location || item._id;
};

const matchRefParent = (item, matchKey, parentId) => {
  if (!parentId) return true;
  const ref = item[matchKey];
  const refId = typeof ref === "object" ? ref._id : ref;
  return String(refId) === String(parentId);
};

const AdminFormFields = ({ schema, values, onChange, mode = "edit", errors = {}, onRefsLoadingChange }) => {
  const [refOptions, setRefOptions] = useState({});
  const [refsLoading, setRefsLoading] = useState(false);

  const refResources = useMemo(
    () => [...new Set(schema.filter((f) => f.ref).map((f) => f.ref))],
    [schema]
  );

  useEffect(() => {
    onRefsLoadingChange?.(refsLoading);
  }, [refsLoading, onRefsLoadingChange]);

  useEffect(() => {
    if (!refResources.length) return undefined;

    let cancelled = false;
    setRefsLoading(true);
    (async () => {
      const entries = await Promise.all(
        refResources.map(async (resource) => {
          try {
            const res = await crudApi(resource).list();
            return [resource, res.data || []];
          } catch {
            return [resource, []];
          }
        })
      );
      if (!cancelled) {
        setRefOptions(Object.fromEntries(entries));
        setRefsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refResources]);

  const visible = schema.filter((field) => {
    if (field.showOn === "create") return mode === "create";
    if (field.showOn === "edit") return mode === "edit";
    return true;
  });

  const setValue = (name, val) => onChange({ ...values, [name]: val });

  const schemaFieldNames = useMemo(
    () => new Set(visible.map((field) => field.name)),
    [visible]
  );

  const getCascadeForField = (field) => {
    const cascade = REF_CASCADE[field.name];
    if (!cascade || !schemaFieldNames.has(cascade.parentField)) return null;
    return cascade;
  };

  const getFilteredOptions = (field) => {
    const all = refOptions[field.ref] || [];
    const cascade = getCascadeForField(field);
    if (!cascade) return all;
    const parentId = values[cascade.parentField];
    if (!parentId) return [];
    return all.filter((item) => matchRefParent(item, cascade.matchKey, parentId));
  };

  if (!visible.length) {
    return (
      <div className="admin-form-empty">
        <p>No editable fields for this section yet.</p>
        <p className="admin-form-empty-hint">
          Run <code>npm run sync-config</code> in the backend folder to load default content from Json files.
        </p>
      </div>
    );
  }

  const renderToggle = (field) => {
    if (!field?.name) return null;
    const fieldId = `field-${field.name.replace(/\./g, "-")}`;
    const err = errors[field.name];

    return (
      <div className={`admin-toggle-item ${err ? "admin-toggle-item--error" : ""}`} key={field.name}>
        <label className="admin-toggle-row" htmlFor={fieldId}>
          <div className="admin-toggle-row__text">
            <span className="admin-toggle-label">
              {field.label}
              {field.required && <span className="admin-required">*</span>}
            </span>
            {field.hint && <span className="admin-toggle-hint">{field.hint}</span>}
          </div>
          <div className="admin-toggle-switch">
            <input
              id={fieldId}
              type="checkbox"
              checked={Boolean(values[field.name])}
              onChange={(e) => setValue(field.name, e.target.checked)}
            />
            <span className="admin-toggle-track" aria-hidden="true" />
          </div>
        </label>
        {err && <span className="admin-field-error">{err}</span>}
      </div>
    );
  };

  const renderField = (field) => {
    if (!field?.name) return null;
    const fieldId = `field-${field.name.replace(/\./g, "-")}`;
    const err = errors[field.name];
    const fullWidth = isFullWidthField(field);

    return (
      <div
        className={`admin-field ${fullWidth ? "admin-field--full" : ""} ${err ? "admin-field--error" : ""}`}
        key={field.name}
      >
        <label className="admin-field-label" htmlFor={fieldId}>
          {field.label}
          {field.required && <span className="admin-required">*</span>}
        </label>

        {field.type === "textarea" ? (
          <textarea
            id={fieldId}
            rows={field.rows || 4}
            value={values[field.name] ?? ""}
            onChange={(e) => setValue(field.name, e.target.value)}
            placeholder={field.hint || ""}
          />
        ) : field.type === "select" ? (
          <select
            id={fieldId}
            value={values[field.name] ?? field.default ?? ""}
            onChange={(e) => setValue(field.name, e.target.value)}
          >
            {(field.options || []).map((opt) => {
              const value = typeof opt === "object" ? opt.value : opt;
              const label =
                typeof opt === "object"
                  ? opt.label
                  : field.optionLabels?.[opt] ||
                    `${opt.charAt(0).toUpperCase()}${opt.slice(1)}`;
              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        ) : field.type === "ref" ? (
          (() => {
            const cascade = getCascadeForField(field);
            const waitingForParent = cascade && !values[cascade.parentField];
            const parentLabel = cascade
              ? visible.find((f) => f.name === cascade.parentField)?.label || cascade.parentField
              : "";
            return (
          <select
            id={fieldId}
            value={values[field.name] ?? ""}
            onChange={(e) => {
              const next = { ...values, [field.name]: e.target.value };
              Object.entries(REF_CASCADE).forEach(([child, cfg]) => {
                if (cfg.parentField === field.name && schemaFieldNames.has(child)) next[child] = "";
              });
              onChange(next);
            }}
            disabled={refsLoading || waitingForParent}
          >
            <option value="">
              {waitingForParent
                ? `Select ${parentLabel} first…`
                : "— Select an option —"}
            </option>
            {getFilteredOptions(field).map((item) => (
              <option key={item._id} value={item._id}>
                {getRefLabel(item, field.ref)}
              </option>
            ))}
          </select>
            );
          })()
        ) : field.type === "refMulti" ? (
          <div className="admin-checklist">
            {refsLoading ? (
              <span className="admin-muted">Loading…</span>
            ) : (refOptions[field.ref] || []).length === 0 ? (
              <span className="admin-muted">No options available</span>
            ) : (
              (refOptions[field.ref] || []).map((item) => {
                const selected = (values[field.name] || []).includes(item._id);
                return (
                  <label key={item._id} className="admin-check-item">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => {
                        const current = values[field.name] || [];
                        const next = e.target.checked
                          ? [...current, item._id]
                          : current.filter((id) => id !== item._id);
                        setValue(field.name, next);
                      }}
                    />
                    <span>{getRefLabel(item, field.ref)}</span>
                  </label>
                );
              })
            )}
          </div>
        ) : field.type === "lines" || field.type === "kvLines" || field.type === "json" ? (
          <textarea
            id={fieldId}
            className={`admin-lines-input${field.type === "json" ? " admin-json-input" : ""}`}
            rows={field.rows || (field.type === "json" ? 10 : 4)}
            placeholder={field.hint || "One item per line"}
            value={values[field.name] ?? ""}
            onChange={(e) => setValue(field.name, e.target.value)}
          />
        ) : field.type === "date" ? (
          <input
            id={fieldId}
            type="date"
            value={values[field.name] ?? ""}
            onChange={(e) => setValue(field.name, e.target.value)}
          />
        ) : field.type === "image" ? (
          <>
            <input
              id={fieldId}
              type="url"
              value={values[field.name] ?? ""}
              onChange={(e) => setValue(field.name, e.target.value)}
              placeholder={field.hint || "https://example.com/logo.png"}
            />
            <AdminImageUpload
              value={values[field.name]}
              onChange={(next) => setValue(field.name, next)}
              buttonLabel={field.uploadLabel || "Upload logo"}
            />
          </>
        ) : field.name === "images" && field.type === "lines" ? (
          <>
            <textarea
              id={fieldId}
              className="admin-lines-input"
              rows={field.rows || 4}
              placeholder={field.hint || "One image URL per line"}
              value={values[field.name] ?? ""}
              onChange={(e) => setValue(field.name, e.target.value)}
            />
            <AdminImageUpload
              multiple
              value={values[field.name]}
              onChange={(next) => setValue(field.name, next)}
            />
            {String(values[field.name] ?? "")
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean)
              .length > 0 && (
              <div className="admin-image-preview-grid">
                {String(values[field.name] ?? "")
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((src, index) => (
                    <img
                      key={`${src.slice(0, 32)}-${index}`}
                      src={resolveImage(src)}
                      alt={`Car photo ${index + 1}`}
                      className="admin-form-preview admin-form-preview--grid"
                    />
                  ))}
              </div>
            )}
          </>
        ) : (
          <input
            id={fieldId}
            type={
              field.type === "password"
                ? "password"
                : field.type === "number"
                  ? "number"
                  : field.type === "email"
                    ? "email"
                    : field.type === "tel"
                      ? "tel"
                      : field.type === "url"
                        ? "url"
                        : "text"
            }
            value={values[field.name] ?? ""}
            onChange={(e) => setValue(field.name, e.target.value)}
            placeholder={field.hint || ""}
          />
        )}

        {field.hint && field.type !== "lines" && field.type !== "kvLines" && field.type !== "json" && (
          <span className="admin-field-hint">{field.hint}</span>
        )}
        {(field.type === "lines" || field.type === "kvLines" || field.type === "json") && field.hint && (
          <span className="admin-field-hint">{field.hint}</span>
        )}
        {err && <span className="admin-field-error">{err}</span>}

        {(field.type === "image" ||
          field.name.includes("image") ||
          field.name.includes("Image")) &&
          values[field.name] && (
            <img
              src={resolveImage(values[field.name])}
              alt="Preview"
              className="admin-form-preview"
            />
          )}
      </div>
    );
  };

  const groups = groupFieldsForRender(visible);

  return (
    <div className="admin-form-fields">
      {refsLoading && refResources.length > 0 && (
        <p className="admin-form-loading">Loading dropdown options…</p>
      )}

      {groups.map((group) => {
        if (group.type === "categoryBlock") {
          return (
            <div className="admin-inspection-category" key={group.section.name}>
              <h4 className="admin-inspection-category__title">{group.section.label}</h4>
              <div className="admin-inspection-category__grid">
                {group.fields.map(renderField)}
              </div>
            </div>
          );
        }

        if (group.type === "section") {
          return (
            <div className="admin-field admin-field--section" key={group.field.name}>
              <h3 className="admin-field-section">{group.field.label}</h3>
            </div>
          );
        }

        if (group.type === "toggleGroup") {
          return (
            <div className="admin-field admin-field--toggle-group" key={`toggles-${group.fields[0].name}`}>
              <div className="admin-toggle-grid">
                {group.fields.map(renderToggle)}
              </div>
            </div>
          );
        }

        return renderField(group.field);
      })}
    </div>
  );
};

export default AdminFormFields;
